import React from 'react';
import { Card } from '@/components/ui/card';
import { LocationDescription } from '@/data/locationDescriptions';
import { MapPin, AlertTriangle, Sparkles } from 'lucide-react';

interface LocationExplorerProps {
  location: LocationDescription;
}

export const LocationExplorer: React.FC<LocationExplorerProps> = ({ location }) => {
  return (
    <Card className="p-6 bg-gradient-to-br from-slate-800 to-slate-900 border-orange-500 space-y-6">
      {/* Location Header */}
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <MapPin className="text-orange-500 w-6 h-6" />
          <h2 className="text-3xl font-bold text-orange-500">{location.name}</h2>
        </div>
        <p className="text-gray-300 text-base leading-relaxed">{location.description}</p>
      </div>

      {/* Atmosphere */}
      <div className="border-l-4 border-orange-500 pl-4 py-2">
        <p className="text-gray-400 text-sm font-semibold mb-2">Atmosphere</p>
        <p className="text-gray-300 italic">{location.atmosphere}</p>
      </div>

      {/* Features */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Sparkles className="text-yellow-400 w-5 h-5" />
          <h3 className="text-yellow-400 font-bold">Notable Features</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {location.features.map((feature, idx) => (
            <div key={idx} className="bg-slate-700 p-2 rounded border border-yellow-600/30">
              <p className="text-gray-200 text-sm">✦ {feature}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Dangers */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <AlertTriangle className="text-red-400 w-5 h-5" />
          <h3 className="text-red-400 font-bold">Dangers & Challenges</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {location.dangers.map((danger, idx) => (
            <div key={idx} className="bg-red-900/20 p-2 rounded border border-red-600/30">
              <p className="text-red-300 text-sm">⚠ {danger}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Interactive Prompt */}
      <div className="bg-slate-700/50 border border-gray-600 rounded p-4 text-center">
        <p className="text-gray-300 text-sm">
          You stand at the edge of <span className="text-orange-400 font-semibold">{location.name}</span>. 
          The world awaits your exploration.
        </p>
      </div>
    </Card>
  );
};
